import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDownload } from '../api/client';
import type {
  AssignmentSummaryRow,
  AttemptPage,
  AttemptSource,
  LearnerSummaryRow,
} from '../api/types';
import { useApi } from '../lib/use-api';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
  pickTitle,
} from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { InteractionsDialog } from '../components/InteractionsDialog';

const PAGE_SIZE = 25;
const SOURCES: readonly AttemptSource[] = ['portal', 'lti', 'scorm', 'xapi'];
type Tab = 'attempts' | 'byAssignment' | 'byLearner';

/**
 * Báo cáo — plan §5.2.
 *
 * Nhãn cột Excel được gửi kèm trong body lúc export: backend không giữ bản
 * dịch của UI, và trả chuỗi tiếng Việt cứng từ backend là điều CLAUDE.md §6
 * cấm. Quên gửi thì file Excel hiện khóa i18n trần — cố ý, để thấy ngay.
 */
export function ReportsPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [tab, setTab] = useState<Tab>('attempts');
  const [inspecting, setInspecting] = useState<
    { attemptId: string; learnerName: string } | undefined
  >(undefined);
  const [source, setSource] = useState<AttemptSource | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [submittedOnly, setSubmittedOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const filter = useMemo(
    () => ({
      source,
      from,
      to,
      submittedOnly: submittedOnly ? 'true' : '',
      limit: PAGE_SIZE,
      offset,
    }),
    [source, from, to, submittedOnly, offset],
  );

  const attempts = useApi<AttemptPage>(
    '/api/reports/attempts',
    { query: filter },
    tab === 'attempts',
  );
  const byAssignment = useApi<{ rows: AssignmentSummaryRow[] }>(
    '/api/reports/by-assignment',
    { query: filter },
    tab === 'byAssignment',
  );
  const byLearner = useApi<{ rows: LearnerSummaryRow[] }>(
    '/api/reports/by-learner',
    { query: filter },
    tab === 'byLearner',
  );

  const exportExcel = async (): Promise<void> => {
    setExporting(true);
    try {
      await apiDownload('/api/reports/export', {
        method: 'POST',
        query: filter,
        fallbackFileName: 'attempts.xlsx',
        body: {
          locale,
          labels: {
            learner: t('report.column.learner'),
            assignment: t('report.column.assignment'),
            source: t('report.column.source'),
            startedAt: t('report.column.startedAt'),
            submittedAt: t('report.column.submittedAt'),
            durationSec: t('report.column.durationSec'),
            scoreRaw: t('report.column.scoreRaw'),
            scoreMax: t('report.column.scoreMax'),
            scoreScaled: t('report.column.scoreScaled'),
            completion: t('report.column.completion'),
            success: t('report.column.success'),
            language: t('report.column.language'),
            sheetName: t('report.sheetName'),
          },
        },
      });
    } finally {
      setExporting(false);
    }
  };

  const changeTab = (next: Tab): void => {
    setTab(next);
    setOffset(0);
  };

  return (
    <section>
      <h1>{t('report.heading')}</h1>

      <div className="toolbar toolbar--wrap">
        <label className="field field--inline">
          <span>{t('report.filter.source')}</span>
          <select
            value={source}
            onChange={(event) => {
              setSource(event.target.value as AttemptSource | '');
              setOffset(0);
            }}
          >
            <option value="">{t('common:action.clear')}</option>
            {SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(`report.source.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field field--inline">
          <span>{t('report.filter.from')}</span>
          <input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setOffset(0);
            }}
          />
        </label>

        <label className="field field--inline">
          <span>{t('report.filter.to')}</span>
          <input
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setOffset(0);
            }}
          />
        </label>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={submittedOnly}
            onChange={(event) => {
              setSubmittedOnly(event.target.checked);
              setOffset(0);
            }}
          />
          <span>{t('report.filter.submittedOnly')}</span>
        </label>

        <button type="button" disabled={exporting} onClick={() => void exportExcel()}>
          {t('report.exportExcel')}
        </button>
      </div>

      <div className="tabs" role="tablist">
        {(['attempts', 'byAssignment', 'byLearner'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? 'tab tab--active' : 'tab'}
            onClick={() => changeTab(value)}
          >
            {t(`report.tab.${value}`)}
          </button>
        ))}
      </div>

      {tab === 'attempts' ? (
        <AttemptsTable
          state={attempts}
          locale={locale}
          onPage={setOffset}
          onInspect={(attemptId, learnerName) => setInspecting({ attemptId, learnerName })}
        />
      ) : null}
      {tab === 'byAssignment' ? <AssignmentTable state={byAssignment} locale={locale} /> : null}
      {tab === 'byLearner' ? <LearnerTable state={byLearner} locale={locale} /> : null}

      {inspecting ? (
        <InteractionsDialog
          attemptId={inspecting.attemptId}
          learnerName={inspecting.learnerName}
          onClose={() => setInspecting(undefined)}
        />
      ) : null}
    </section>
  );
}

/** Chuỗi '1'/'0' của cột TINYINT → dấu tích. Number() trước: '0' là truthy. */
function flag(value: string | null): string {
  if (value === null) return '—';
  return Number(value) === 1 ? '✓' : '—';
}

function AttemptsTable({
  state,
  locale,
  onPage,
  onInspect,
}: {
  state: ReturnType<typeof useApi<AttemptPage>>;
  locale: string;
  onPage: (offset: number) => void;
  onInspect: (attemptId: string, learnerName: string) => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;
  if (state.data.rows.length === 0) return <EmptyNotice />;

  return (
    <>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t('report.column.learner')}</th>
            <th scope="col">{t('report.column.assignment')}</th>
            <th scope="col">{t('report.column.source')}</th>
            <th scope="col">{t('report.column.startedAt')}</th>
            <th scope="col">{t('report.column.durationSec')}</th>
            <th scope="col">{t('report.column.scoreScaled')}</th>
            <th scope="col">{t('report.column.completion')}</th>
            <th scope="col">{t('report.column.success')}</th>
            <th scope="col">{t('library.column.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {state.data.rows.map((row) => (
            <tr key={row.id}>
              <td>{row.learner_name}</td>
              <td>{pickTitle(row.assignment_title, locale)}</td>
              <td>{t(`report.source.${row.source}`)}</td>
              <td>{formatDateTime(row.started_at, locale)}</td>
              <td>{formatDuration(row.duration_sec, locale)}</td>
              <td>
                {row.score_scaled === null ? '—' : formatPercent(Number(row.score_scaled), locale)}
              </td>
              <td>{flag(row.completion)}</td>
              <td>{flag(row.success)}</td>
              <td className="actions">
                <button type="button" onClick={() => onInspect(row.id, row.learner_name)}>
                  {t('report.viewInteractions')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        total={state.data.total}
        limit={state.data.limit}
        offset={state.data.offset}
        onChange={onPage}
      />
    </>
  );
}

function AssignmentTable({
  state,
  locale,
}: {
  state: ReturnType<typeof useApi<{ rows: AssignmentSummaryRow[] }>>;
  locale: string;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;
  if (state.data.rows.length === 0) return <EmptyNotice />;

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th scope="col">{t('report.column.assignment')}</th>
          <th scope="col">{t('report.column.learners')}</th>
          <th scope="col">{t('report.column.attempts')}</th>
          <th scope="col">{t('report.column.completionRate')}</th>
          <th scope="col">{t('report.column.averageScore')}</th>
          {/* Bốn cột kênh là bằng chứng "ba kênh về một bảng" (§9). */}
          <th scope="col">{t('report.source.portal')}</th>
          <th scope="col">{t('report.source.lti')}</th>
          <th scope="col">{t('report.source.scorm')}</th>
          <th scope="col">{t('report.source.xapi')}</th>
        </tr>
      </thead>
      <tbody>
        {state.data.rows.map((row) => (
          <tr key={row.assignment_id}>
            <td>{pickTitle(row.title_i18n, locale)}</td>
            <td>{formatNumber(row.learner_count, locale)}</td>
            <td>{formatNumber(row.attempt_count, locale)}</td>
            <td>
              {row.attempt_count === 0
                ? '—'
                : formatPercent(row.completed_count / row.attempt_count, locale)}
            </td>
            <td>{row.avg_scaled === null ? '—' : formatPercent(Number(row.avg_scaled), locale)}</td>
            <td>{formatNumber(row.portal_count, locale)}</td>
            <td>{formatNumber(row.lti_count, locale)}</td>
            <td>{formatNumber(row.scorm_count, locale)}</td>
            <td>{formatNumber(row.xapi_count, locale)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LearnerTable({
  state,
  locale,
}: {
  state: ReturnType<typeof useApi<{ rows: LearnerSummaryRow[] }>>;
  locale: string;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;
  if (state.data.rows.length === 0) return <EmptyNotice />;

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th scope="col">{t('report.column.learner')}</th>
          <th scope="col">{t('report.column.assignment')}</th>
          <th scope="col">{t('report.column.attempts')}</th>
          <th scope="col">{t('report.column.completionRate')}</th>
          <th scope="col">{t('report.column.averageScore')}</th>
          <th scope="col">{t('report.column.submittedAt')}</th>
        </tr>
      </thead>
      <tbody>
        {state.data.rows.map((row) => (
          <tr key={row.principal_id}>
            <td>{row.learner_name}</td>
            <td>{formatNumber(row.assignment_count, locale)}</td>
            <td>{formatNumber(row.attempt_count, locale)}</td>
            <td>
              {row.attempt_count === 0
                ? '—'
                : formatPercent(row.completed_count / row.attempt_count, locale)}
            </td>
            <td>{row.avg_scaled === null ? '—' : formatPercent(Number(row.avg_scaled), locale)}</td>
            <td>{formatDateTime(row.last_activity, locale)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
